// Gestion de la connexion côté navigateur.
// Expose l'état (chargement / connecté / non connecté) et les actions inscription,
// connexion, déconnexion. Au démarrage, on demande au serveur qui est connecté (/me).

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/lib/api'

// Vue d'un utilisateur côté client (les dates arrivent en texte ISO via JSON).
export interface SessionUser {
  id: string
  email: string
  emailVerified: boolean
  role: 'user' | 'admin'
  tier: 'trial' | 'starter' | 'premium'
  // Modules déverrouillés (axe DISTINCT du tier) : 'savon' (socle) et/ou 'cosmetique'
  // (add-on Stripe, Lot 3). Déjà renvoyé par /api/auth/me via PublicUser. Consommé par le
  // sélecteur/upsell savon-cosmétique et le garde de nav (moduleRequis) au Lot 2c.
  modules: string[]
  trialEndsAt: string | null
  // Abonnement Stripe (déjà renvoyés par /api/auth/me — utilisés par le mur d'accès S3).
  subscriptionStatus: string | null
  currentPeriodEnd: string | null
  createdAt: string
  updatedAt: string
}

type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

interface AuthContextValue {
  status: AuthStatus
  user: SessionUser | null
  register: (email: string, password: string) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<SessionUser | null>(null)

  // Au montage : qui est connecté ?
  useEffect(() => {
    api<{ user: SessionUser }>('/api/auth/me')
      .then((data) => {
        setUser(data.user)
        setStatus('authenticated')
      })
      .catch(() => {
        setUser(null)
        setStatus('anonymous')
      })
  }, [])

  async function register(email: string, password: string): Promise<void> {
    const data = await api<{ user: SessionUser }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    setUser(data.user)
    setStatus('authenticated')
  }

  async function login(email: string, password: string): Promise<void> {
    const data = await api<{ user: SessionUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    setUser(data.user)
    setStatus('authenticated')
  }

  async function logout(): Promise<void> {
    await api('/api/auth/logout', { method: 'POST' })
    setUser(null)
    setStatus('anonymous')
  }

  return (
    <AuthContext.Provider value={{ status, user, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth doit être utilisé à l\'intérieur de <AuthProvider>.')
  }
  return ctx
}
